import React, { useState, useMemo } from "react";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import {
  Input,
  Spin,
  Typography,
  Divider,
  Table,
  Tag,
  Button,
  Switch,
} from "antd";
import moment from "moment";
import { CSVLink } from "react-csv";
import { AiOutlineDownload } from "react-icons/ai";
import { AiOutlineSearch } from "react-icons/ai";
import { useGetInstructorReport } from "../../../../../services/report.service";
import { useStaffGroup } from "./StaffGroupContext";

const { Text } = Typography;

// Week 0 = days 1–7, Week 1 = days 8–14, etc.
function getWeeksInMonth(monthMoment) {
  const lastDay = monthMoment.clone().endOf("month").date();
  return Math.ceil(lastDay / 7);
}

function getWeekRange(monthMoment, weekIndex) {
  const lastDay = monthMoment.clone().endOf("month").date();
  const startDay = weekIndex * 7 + 1;
  const endDay = Math.min((weekIndex + 1) * 7, lastDay);
  return {
    start: monthMoment.clone().date(startDay).startOf("day"),
    end: monthMoment.clone().date(endDay).endOf("day"),
  };
}

function getCurrentWeekIndex(monthMoment) {
  const now = moment();
  if (!now.isSame(monthMoment, "month")) return 0;
  return Math.floor((now.date() - 1) / 7);
}

const Report = () => {
  const group = useStaffGroup();
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [selectedWeek, setSelectedWeek] = useState(() =>
    getCurrentWeekIndex(moment()),
  );
  const [isLiveView, setIsLiveView] = useState(false);
  const [instructorSearch, setInstructorSearch] = useState("");

  const totalWeeks = getWeeksInMonth(selectedMonth);
  const { start: weekStart, end: weekEnd } = getWeekRange(
    selectedMonth,
    selectedWeek,
  );

  // Fetch the full month — filtered to selected week on the client
  const queryParams = {
    instructor_id: undefined,
    group,
    start_date: selectedMonth.clone().startOf("month").format("YYYY-MM-DD"),
    end_date: selectedMonth.clone().endOf("month").format("YYYY-MM-DD"),
  };

  const {
    data: reportData,
    isLoading,
    error,
    isFetching,
  } = useGetInstructorReport(queryParams, {
    enabled: true,
    refetchInterval: isLiveView ? 60000 : false,
    refetchOnWindowFocus: isLiveView,
  });

  const onMonthChange = (monthMoment) => {
    if (!monthMoment) return;
    setSelectedMonth(monthMoment);
    setSelectedWeek(getCurrentWeekIndex(monthMoment));
  };

  // Days visible in the selected week (used for table display).
  // Always padded to 7 slots so partial weeks keep consistent column alignment.
  const allDates = useMemo(() => {
    const dates = [];
    let current = weekStart.clone();
    while (current.isSameOrBefore(weekEnd, "day")) {
      dates.push({
        dateStr: current.format("YYYY-MM-DD"),
        displayDate: current.format("D/M"),
        dayName: current.format("ddd"),
        isToday: current.isSame(moment(), "day"),
        moment: current.clone(),
        isPlaceholder: false,
      });
      current.add(1, "day");
    }
    // Pad remaining slots with next-month days (rendered as dimmed, no data)
    while (dates.length < 7) {
      dates.push({
        dateStr: current.format("YYYY-MM-DD"),
        displayDate: current.format("D/M"),
        dayName: current.format("ddd"),
        isToday: false,
        moment: current.clone(),
        isPlaceholder: true,
      });
      current.add(1, "day");
    }
    return dates;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedWeek]);

  // All days in the selected month — used for CSV export only
  const allMonthDates = useMemo(() => {
    const dates = [];
    let current = selectedMonth.clone().startOf("month");
    const end = selectedMonth.clone().endOf("month");
    while (current.isSameOrBefore(end, "day")) {
      dates.push({
        dateStr: current.format("YYYY-MM-DD"),
        displayDate: current.format("D/M"),
        dayName: current.format("ddd"),
      });
      current.add(1, "day");
    }
    return dates;
  }, [selectedMonth]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "leave":
        return <Tag color="orange">L</Tag>;
      case "sick":
        return <Tag color="red">S</Tag>;
      case "half_day":
        return <Tag color="blue">H</Tag>;
      case "permission":
        return <Tag color="blue">P</Tag>;
      case "check_in":
        return <Tag color="cyan">L</Tag>;
      case "check_out":
        return <Tag color="cyan">E</Tag>;
      case "on_time":
        return <Tag color="green">P</Tag>;
      case "short_time":
        return <Tag color="gold">M</Tag>;
      case "missed":
        return <Tag color="red">W</Tag>;
      case "waiting":
      case "":
        return <Tag color="default">-</Tag>;
      case "holiday":
        return <Tag color="pink">H</Tag>;
      default:
        return null;
    }
  };

  const resolveDisplayStatus = (dateRecord) => {
    let displayStatus = dateRecord?.status;
    const eh = dateRecord?.excused_hours;

    // Normalise alias statuses so cellValue has one consistent set to handle
    if (displayStatus === "sick_leave") displayStatus = "sick";
    if (
      displayStatus === "half_day_morning" ||
      displayStatus === "half_day_afternoon"
    )
      displayStatus = "half_day";

    const isAbsentOrUnmarked =
      !displayStatus ||
      ["unmarked", "missed", "absent", "waiting", ""].includes(displayStatus);

    // Only try to derive status from excused_hours when there's no explicit status
    if (eh && isAbsentOrUnmarked) {
      if (eh.leave_days > 0) displayStatus = "leave";
      else if (eh.sick_days > 0) displayStatus = "sick";
      else if (eh.half_day_hours && eh.half_day_hours !== 0)
        displayStatus = "half_day";
      else if (eh.permission_hours && eh.permission_hours !== 0)
        displayStatus = "permission";
    }
    return displayStatus;
  };

  const columns = useMemo(() => {
    if (!reportData?.data?.instructors || allDates.length === 0) return [];

    const dateColumns = allDates.map((date) => ({
      title: date.isPlaceholder ? (
        <div className="text-center opacity-30">
          <div className="font-bold text-xs text-gray-400">
            {date.displayDate}
          </div>
          <div className="text-[10px] lowercase text-gray-400">
            {date.dayName}
          </div>
        </div>
      ) : (
        <div
          className={`text-center ${date.isToday ? "bg-orange-50 rounded" : ""}`}
        >
          <div
            className={`font-bold text-xs ${date.isToday ? "text-orange-500" : "text-gray-800"}`}
          >
            {date.displayDate}
          </div>
          <div
            className={`text-[10px] lowercase ${date.isToday ? "text-orange-400 font-bold" : "text-blue-500"}`}
          >
            {date.dayName}
          </div>
          {date.isToday && (
            <div className="text-[8px] text-orange-400 font-bold tracking-tight">
              TODAY
            </div>
          )}
        </div>
      ),
      key: date.dateStr,
      width: 90,
      render: (_, record) => {
        if (date.isPlaceholder) {
          return (
            <div className="flex items-center justify-center py-3 bg-gray-50">
              <span className="text-gray-200 text-base select-none">—</span>
            </div>
          );
        }

        const dateRecord = record.records?.find((r) => r.date === date.dateStr);
        const displayStatus = resolveDisplayStatus(dateRecord);

        return (
          <div
            className={`flex flex-col items-center px-1 ${date.isToday ? "bg-orange-50 rounded" : ""}`}
          >
            <div
              className={`font-bold text-sm ${
                dateRecord?.status === "missed"
                  ? "text-red-500"
                  : "text-gray-800"
              }`}
            >
              {dateRecord?.ah_display || "0:00"}
            </div>
            <div className="w-full border-t border-black my-1"></div>
            <div
              className={`text-xs font-medium ${
                dateRecord?.excused_hours?.eh_display &&
                dateRecord.excused_hours.eh_display !== "0:00"
                  ? "text-blue-600"
                  : "text-gray-400"
              }`}
            >
              {dateRecord?.excused_hours?.eh_display || "0:00"}
            </div>
            {displayStatus && displayStatus !== "missed" && (
              <div className="mt-1">{getStatusBadge(displayStatus)}</div>
            )}
          </div>
        );
      },
    }));

    return [
      {
        title: (
          <div className="flex flex-col items-center py-1">
            <div className="font-black text-gray-800 uppercase text-[11px] tracking-wider">
              Instructor
            </div>
          </div>
        ),
        dataIndex: "instructor_name",
        key: "instructor_name",
        width: 180,
        render: (text) => (
          <div className="flex items-center justify-between gap-2 w-full px-2">
            <div className="font-bold text-gray-800 truncate">{text}</div>
            <div className="flex flex-col items-center flex-shrink-0 bg-gray-50 rounded px-1.5 py-0.5 border border-gray-200 min-w-[32px]">
              <div className="text-[9px] text-blue-500 font-black uppercase tracking-tighter">
                AH
              </div>
              <div className="w-full border-t border-black my-0.5"></div>
              <div className="text-[9px] text-blue-500 font-black uppercase tracking-tighter">
                EH
              </div>
            </div>
          </div>
        ),
      },
      ...dateColumns,
      {
        title: (
          <div className="flex flex-col items-center py-1">
            <div className="font-black text-gray-800 uppercase text-[11px] tracking-wider">
              Week Total
            </div>
          </div>
        ),
        key: "week_total",
        width: 110,
        render: (_, record) => {
          let ahMinutes = 0;
          let ehMinutes = 0;
          allDates
            .filter((d) => !d.isPlaceholder)
            .forEach(({ dateStr }) => {
              const rec = record.records?.find((r) => r.date === dateStr);
              if (rec?.ah_display && rec.ah_display !== "0:00") {
                const [h, m] = rec.ah_display.split(":").map(Number);
                ahMinutes += h * 60 + (m || 0);
              }
              if (rec?.eh_display && rec.eh_display !== "0:00") {
                const [h, m] = rec.eh_display.split(":").map(Number);
                ehMinutes += h * 60 + (m || 0);
              }
            });
          const ahLabel = `${Math.floor(ahMinutes / 60)}:${String(ahMinutes % 60).padStart(2, "0")}`;
          const ehLabel = `${Math.floor(ehMinutes / 60)}:${String(ehMinutes % 60).padStart(2, "0")}`;
          return (
            <div className="flex flex-col items-center px-1">
              <div className="font-bold text-lg text-orange-600">{ahLabel}</div>
              <div className="w-full border-t border-black my-1"></div>
              <div className="text-sm font-bold text-blue-500">{ehLabel}</div>
            </div>
          );
        },
      },
    ];
  }, [reportData, allDates]);

  const tableData = useMemo(() => {
    if (!reportData?.data?.instructors) return [];
    return reportData.data.instructors.map((instructor) => {
      let totalEhMinutes = 0;
      instructor.records?.forEach((rec) => {
        const eh = rec.excused_hours?.eh_display || "0:00";
        if (eh !== "0:00") {
          const [h, m] = eh.split(":").map(Number);
          totalEhMinutes += h * 60 + m;
        }
      });
      const h = Math.floor(totalEhMinutes / 60);
      const m = totalEhMinutes % 60;
      return {
        key: instructor.instructor_id,
        ...instructor,
        total_eh_hours: `${h}:${m.toString().padStart(2, "0")}`,
      };
    });
  }, [reportData]);

  const filteredTableData = useMemo(() => {
    if (!instructorSearch.trim()) return tableData;
    const lower = instructorSearch.toLowerCase();
    return tableData.filter((row) =>
      row.instructor_name?.toLowerCase().includes(lower),
    );
  }, [tableData, instructorSearch]);

  const csvData = useMemo(() => {
    if (!filteredTableData.length || allMonthDates.length === 0) return [];

    const cellValue = (rec, displayStatus) => {
      const ah = rec?.ah_display || "0:00";
      const ehDisplay = rec?.excused_hours?.eh_display || "0:00";
      const hasEH = ehDisplay !== "0:00";
      const s = displayStatus;

      if (!s || s === "unmarked" || s === "waiting") return "—";
      if (s === "holiday") return "Holiday";
      if (s === "missed" || s === "absent") return "Absent";
      if (s === "leave") return hasEH ? `Leave (${ehDisplay})` : "Leave";
      if (s === "sick") return hasEH ? `Sick (${ehDisplay})` : "Sick";
      if (s === "half_day") {
        const base = hasEH ? `Half Day (${ehDisplay})` : "Half Day";
        return ah !== "0:00" ? `${base} | AH ${ah}` : base;
      }
      if (s === "permission") {
        return ah !== "0:00"
          ? `${ah} | Perm ${ehDisplay}`
          : hasEH
            ? `Permission (${ehDisplay})`
            : "Permission";
      }
      if (s === "short_time") {
        return hasEH ? `Short ${ah} | EH ${ehDisplay}` : `Short ${ah}`;
      }
      if (s === "check_in") return `Late-In ${ah}`;
      if (s === "check_out") return `Early-Out ${ah}`;
      // on_time / present — show AH and any exception hours
      if (hasEH) return `${ah} | EH ${ehDisplay}`;
      return ah !== "0:00" ? ah : "Present";
    };

    const blankRow = { Date: "", Day: "" };

    // Title row
    const rows = [
      {
        Date: `Attendance Report — ${selectedMonth.format("MMMM YYYY")}`,
        Day: "",
      },
      blankRow,
    ];

    // Daily matrix rows
    allMonthDates.forEach((date) => {
      const row = {
        Date: moment(date.dateStr).format("DD MMM YYYY"),
        Day: date.dayName,
      };
      filteredTableData.forEach((inst) => {
        const rec = inst.records?.find((r) => r.date === date.dateStr);
        row[inst.instructor_name] = cellValue(rec, resolveDisplayStatus(rec));
      });
      rows.push(row);
    });

    rows.push(blankRow);

    // Summary rows — one metric per row, one instructor per column
    const totalAhRow = { Date: "Total AH (Actual Hours)", Day: "" };
    const totalEhRow = { Date: "Total EH (Exception Hours)", Day: "" };
    const presentRow = { Date: "Days Present", Day: "" };
    const absentRow = { Date: "Days Absent", Day: "" };
    const leaveRow = { Date: "Days Leave / Sick", Day: "" };
    const shortRow = { Date: "Days Short Time", Day: "" };
    const holidayRow = { Date: "Days Holiday", Day: "" };

    filteredTableData.forEach((inst) => {
      let present = 0,
        absent = 0,
        leave = 0,
        short = 0,
        holiday = 0;
      inst.records?.forEach((rec) => {
        const s = rec.status;
        if (
          [
            "on_time",
            "present",
            "check_in",
            "check_out",
            "permission",
          ].includes(s)
        )
          present++;
        else if (s === "short_time") {
          present++;
          short++;
        } else if (s === "missed" || s === "absent") absent++;
        else if (["leave", "sick", "half_day", "sick_leave"].includes(s))
          leave++;
        else if (s === "holiday") holiday++;
      });
      const n = inst.instructor_name;
      totalAhRow[n] = inst.total_actual_hours || "0:00";
      totalEhRow[n] = inst.total_eh_hours || "0:00";
      presentRow[n] = present || "";
      absentRow[n] = absent || "";
      leaveRow[n] = leave || "";
      shortRow[n] = short || "";
      holidayRow[n] = holiday || "";
    });

    rows.push(
      totalAhRow,
      totalEhRow,
      blankRow,
      presentRow,
      absentRow,
      leaveRow,
      shortRow,
      holidayRow,
    );

    return rows;
  }, [filteredTableData, allMonthDates, selectedMonth]);

  return (
    <div className="mt-8">
      <div className="bg-white drop-shadow-md p-8 rounded-lg w-full">
        <div className="text-2xl text-orange-500 font-semibold py-4">
          Instructor Attendance Report
        </div>

        {/* Controls */}
        <div className="mb-6 flex items-center justify-between bg-gray-50 p-6 rounded-xl border border-gray-100">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <Text strong className="text-gray-600">
                Month
              </Text>
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={onMonthChange}
                format="MMMM YYYY"
                allowClear={false}
                style={{ width: "180px", height: "40px" }}
                className="rounded-lg shadow-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Text strong className="text-gray-600">
                Search Instructor
              </Text>
              <Input
                placeholder="Search by name..."
                prefix={<AiOutlineSearch className="text-gray-400" />}
                allowClear
                style={{ width: "220px", height: "40px" }}
                className="rounded-lg shadow-sm"
                value={instructorSearch}
                onChange={(e) => setInstructorSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm">
              <span className="text-sm font-medium text-gray-700">
                <span className="mr-1">🔴</span> Live View:
              </span>
              <Switch
                size="small"
                checked={isLiveView}
                onChange={setIsLiveView}
                className={isLiveView ? "bg-green-500" : ""}
              />
              {isFetching && isLiveView && (
                <div className="flex items-center ml-3 pl-3 border-l border-gray-200">
                  <Spin size="small" className="mr-2" />
                  <span className="text-xs text-blue-600 font-medium">
                    Updating...
                  </span>
                </div>
              )}
            </div>

            {filteredTableData.length > 0 && (
              <CSVLink
                filename={`instructor_attendance_${selectedMonth.format("MMMM_YYYY")}.csv`}
                data={csvData}
              >
                <Button
                  type="primary"
                  icon={<AiOutlineDownload className="mr-2 text-xl" />}
                  className="bg-green-600 hover:bg-green-700 border-none h-[40px] px-6 rounded-lg flex items-center"
                >
                  Export CSV
                </Button>
              </CSVLink>
            )}
          </div>
        </div>

        {/* Week tabs */}
        <div className="flex gap-2 mb-4">
          {Array.from({ length: totalWeeks }, (_, i) => {
            const { start, end } = getWeekRange(selectedMonth, i);
            const tabLabel = `${start.format("D")}–${end.format("D MMM")}`;
            const isActive = i === selectedWeek;
            return (
              <button
                key={i}
                onClick={() => setSelectedWeek(i)}
                className={`flex flex-col items-center px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-orange-50 border-orange-500 shadow-sm"
                    : "bg-white border-gray-200 hover:border-orange-300"
                }`}
              >
                <span
                  className={`text-xs font-bold ${isActive ? "text-orange-600" : "text-orange-400"}`}
                >
                  W{i + 1}
                </span>
                <span
                  className={`text-[10px] ${isActive ? "text-gray-700 font-semibold" : "text-gray-400"}`}
                >
                  {tabLabel}
                </span>
              </button>
            );
          })}
        </div>

        {/* Weekly summary stats */}

        {/* Table */}
        <div className="mt-2">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Spin size="large" />
              <Text type="secondary" className="animate-pulse">
                Loading records...
              </Text>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-10 bg-red-50 rounded-lg border border-red-200">
              Error fetching report data: {error.message}
            </div>
          ) : allDates.length > 0 ? (
            <div className="space-y-4">
              <Table
                columns={columns}
                dataSource={filteredTableData}
                loading={isLoading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  showTotal: (total, range) =>
                    `${range[0]}–${range[1]} of ${total} instructor${total !== 1 ? "s" : ""}`,
                  position: ["bottomRight"],
                }}
                bordered
                size="middle"
                className="custom-report-table"
                rowClassName="hover:bg-gray-50"
                style={{ borderColor: "#000000" }}
              />

              <Divider className="border-black" />
              <div className="text-xs text-gray-500 mt-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-1">
                    <Tag color="orange">L</Tag>
                    <span>= Leave</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="red">S</Tag>
                    <span>= Sick</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="blue">H</Tag>
                    <span>= Half Day</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="gold">M</Tag>
                    <span>= Short Time</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="red">W</Tag>
                    <span>= Waiting/Missed</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="green">P</Tag>
                    <span>= Present</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="blue">P</Tag>
                    <span>= Permission</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="pink">H</Tag>
                    <span>= Holiday</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="blue">AH</Tag>
                    <span>= Actual Hours</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tag color="blue">EH</Tag>
                    <span>= Exception Hours</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Report;
